"use client";

import type React from "react";
import { useState, useEffect } from "react";
import {
  VStack,
  FormControl,
  FormLabel,
  Input,
  Button,
  IconButton,
  Flex,
  FormErrorMessage,
  Box,
} from "@chakra-ui/react";
import { Plus, X } from "lucide-react";
import type { PostCallAnalysisData } from "./PostCallAnalysis";

interface EnumAnalysisFormProps {
  onSave: (data: PostCallAnalysisData) => void;
  onCancel: () => void;
  initialData?: PostCallAnalysisData;
}

export const EnumAnalysisForm: React.FC<EnumAnalysisFormProps> = ({
  onSave,
  onCancel,
  initialData,
}) => {
  const [name, setName] = useState(initialData?.name || "");
  const [description, setDescription] = useState(
    initialData?.description || ""
  );
  const [choices, setChoices] = useState<string[]>(initialData?.choices || []);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    if (initialData) {
      setName(initialData.name);
      setDescription(initialData.description);
      setChoices(initialData.choices || []);
    }
  }, [initialData]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitted(true);
    if (choices.length === 0 || choices.some((c) => c.trim() === "")) return;

    onSave({
      type: "enum",
      name,
      description,
      choices: choices.filter((c) => c.trim() !== ""),
    });
  };

  const handleAddChoice = () => setChoices((prev) => [...prev, ""]);
  const handleChoiceChange = (index: number, value: string) => {
    const next = [...choices];
    next[index] = value;
    setChoices(next);
  };
  const handleRemoveChoice = (index: number) =>
    setChoices((prev) => prev.filter((_, i) => i !== index));

  const isChoicesInvalid =
    isSubmitted &&
    (choices.length === 0 || choices.some((choice) => choice.trim() === ""));

  return (
    <Box mb={4}>
      <form onSubmit={handleSubmit}>
        <VStack spacing={4} align="stretch">
          <FormControl isRequired>
            <FormLabel fontSize="sm" color="secondary.700">
              Name
            </FormLabel>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="customer_type"
              size="sm"
              variant="outline"
              focusBorderColor="primary.500"
              _placeholder={{ color: "gray.500" }}
            />
          </FormControl>

          <FormControl isRequired>
            <FormLabel fontSize="sm" color="secondary.700">
              Description
            </FormLabel>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What type of customer is the user?"
              size="sm"
              variant="outline"
              focusBorderColor="primary.500"
              _placeholder={{ color: "gray.500" }}
            />
          </FormControl>

          <FormControl isRequired isInvalid={isChoicesInvalid}>
            <FormLabel fontSize="sm" color="secondary.700">
              Choices
            </FormLabel>
            <VStack spacing={2} align="stretch">
              {choices.map((choice, index) => (
                <Flex key={index} gap={2}>
                  <Input
                    value={choice}
                    onChange={(e) => handleChoiceChange(index, e.target.value)}
                    placeholder="1st_time_customer"
                    size="sm"
                    variant="outline"
                    focusBorderColor="primary.500"
                    _placeholder={{ color: "gray.500" }}
                  />
                  <IconButton
                    aria-label="Remove choice"
                    icon={<X size={16} />}
                    size="sm"
                    variant="ghost"
                    onClick={() => handleRemoveChoice(index)}
                    color="gray.600"
                    _hover={{ bg: "gray.100" }}
                    isDisabled={choices.length === 1}
                  />
                </Flex>
              ))}
              <Button
                leftIcon={<Plus size={16} />}
                variant="ghost"
                size="sm"
                onClick={handleAddChoice}
                color="gray.600"
                _hover={{ bg: "gray.100" }}
                alignSelf="flex-start"
              >
                Add
              </Button>
              {isChoicesInvalid && (
                <FormErrorMessage>
                  At least one non-empty choice is required
                </FormErrorMessage>
              )}
            </VStack>
          </FormControl>

          <Flex justify="flex-end" gap={2} pt={4}>
            <Button variant="ghost" size="sm" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              bg="primary.500"
              color="white"
              _hover={{ bg: "primary.600" }}
            >
              Save
            </Button>
          </Flex>
        </VStack>
      </form>
    </Box>
  );
};
